import React, { useState, useMemo, useRef, useCallback } from 'react';
import { Box, Button, Chip, Paper, Stack, Table, Typography } from '@esgian/esgianui';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import { useSelector } from 'react-redux';
import { getSessionStatus } from '@store/features';
import { APP_NAME, DATE_FORMAT } from '@constants/index';
import { useQuery } from '@tanstack/react-query';
import { useTheme } from '@hooks';
import { useTimezone } from '@hooks/useTimezone';
import { getArchivedReports } from '@api/ReportScheduler';
import HistoryDetailsView from '../../SubSections/ReportScheduler/HistoryDetailsView';
import moment from 'moment-timezone';

const PERIOD_LABELS = {
  Last24Hours: 'Last 24 Hours',
  Last7Days: 'Last 7 Days',
  Last14Days: 'Last 14 Days',
  LastMonth: 'Last Month',
  CurrentWell: 'Current Well',
  Custom: 'Custom'
};

function ArchivedTab() {
  const sessionStatus = useSelector(getSessionStatus);
  const [expandedRow, setExpandedRow] = useState([]);
  const [expandedReportId, setExpandedReportId] = useState(null);
  const { selectedTimeZone } = useTimezone();

  const {
    theme: {
      palette: { mode }
    }
  } = useTheme();

  const archivedQuery = useQuery({
    queryKey: ['scheduledReport', 'archived'],
    enabled: sessionStatus === 'ok',
    placeholderData: [],
    queryFn: ({ signal }) => getArchivedReports(signal)
  });

  const archived = archivedQuery.data || [];

  const archivedRef = useRef(archived);
  archivedRef.current = archived;
  const expandedReportIdRef = useRef(expandedReportId);
  expandedReportIdRef.current = expandedReportId;

  const toggleExpandRow = useCallback((reportId) => {
    if (expandedReportIdRef.current === reportId) {
      setExpandedRow([]);
      setExpandedReportId(null);
    } else {
      const dataIndex = archivedRef.current.findIndex((r) => r.id === reportId);
      setExpandedRow(dataIndex >= 0 ? [dataIndex] : []);
      setExpandedReportId(reportId);
    }
  }, []);

  const formatDate = (dateStr) => {
    if (!dateStr) return '--';
    return selectedTimeZone
      ? moment.tz(dateStr, selectedTimeZone).format(DATE_FORMAT)
      : moment(dateStr).format(DATE_FORMAT);
  };

  const columns = useMemo(
    () => [
      {
        name: 'reportName',
        label: 'Title',
        options: {
          sort: true,
          sortThirdClickReset: true,
          customBodyRender: (value) => value || '--'
        }
      },
      {
        name: 'rigName',
        label: 'Content',
        options: {
          sort: true,
          sortThirdClickReset: true,
          customBodyRender: (value, tableMeta) => {
            const report = archived.find((r) => r.id === tableMeta.rowData[5]);
            if (report?.isImmediate) {
              const parts = [report?.rigName, report?.reportType].filter(Boolean);
              return (
                <Box>
                  <Typography variant="body2">{parts.join(', ') || '--'}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Sent immediately
                  </Typography>
                </Box>
              );
            }
            const parts = [report?.rigName, report?.period, report?.reportType].filter(Boolean);
            return (
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {parts.join(', ') || '--'}
              </Typography>
            );
          }
        }
      },
      {
        name: 'frequency',
        label: 'Schedule detail',
        options: {
          sort: true,
          sortThirdClickReset: true,
          customBodyRender: (value, tableMeta) => {
            const report = archived.find((r) => r.id === tableMeta.rowData[5]);
            if (report?.isImmediate) {
              const periodLabel = PERIOD_LABELS[report?.period] || report?.period || '--';
              return (
                <Box>
                  <Typography variant="body2">Immediate</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {periodLabel}
                  </Typography>
                </Box>
              );
            }
            if (!value) return '--';
            const periodLabel = PERIOD_LABELS[report?.period] || report?.period || '';
            return (
              <Box>
                <Typography variant="body2">{`Send out ${value.toLowerCase()}`}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {`From ${formatDate(report?.startDate)}`}
                  {periodLabel ? ` · ${periodLabel}` : ''}
                </Typography>
              </Box>
            );
          }
        }
      },
      {
        name: 'emails',
        label: 'Recipients',
        options: {
          sort: true,
          sortThirdClickReset: true,
          customBodyRender: (value) => {
            if (!Array.isArray(value) || value.length === 0) return '--';
            return <Typography variant="body2">{value.join(', ')}</Typography>;
          },
          sortCompare: (order) => (a, b) => {
            const aVal = Array.isArray(a.data) ? a.data.join(', ') : '';
            const bVal = Array.isArray(b.data) ? b.data.join(', ') : '';
            return aVal.localeCompare(bVal) * (order === 'asc' ? 1 : -1);
          }
        }
      },
      {
        name: 'isActive',
        label: 'Status',
        options: {
          sort: true,
          sortThirdClickReset: true,
          customBodyRender: () => (
            <Chip label="Archived" size="small" color="default" variant="outlined" />
          )
        }
      },
      {
        name: 'id',
        label: 'Action',
        options: {
          sort: false,
          filter: false,
          searchable: false,
          customBodyRender: (value, tableMeta) => {
            const report = archived.find((r) => r.id === value);
            const isExpanded = expandedReportId === report?.id;
            return (
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleExpandRow(report?.id);
                  }}
                  endIcon={
                    isExpanded ? (
                      <ArrowUpwardRoundedIcon />
                    ) : (
                      <ArrowDownwardRoundedIcon />
                    )
                  }
                  size="small"
                  variant="text">
                  <Typography variant="caption">History</Typography>
                </Button>
              </Stack>
            );
          }
        }
      }
    ],
    [archived, expandedReportId, toggleExpandRow]
  );

  const options = useMemo(
    () => ({
      selectableRows: 'none',
      responsive: 'standard',
      search: true,
      download: false,
      print: false,
      viewColumns: false,
      filter: false,
      pagination: true,
      elevation: 0,
      toolbar: true,
      rowsPerPageOptions: [10, 25, 50, 100],
      sortOrder: {
        name: 'reportName',
        direction: 'asc'
      },
      expandableRows: true,
      expandableRowsHeader: false,
      expandableRowsOnClick: false,
      rowsExpanded: expandedRow,
      renderExpandableRow: (rowData, rowMeta) => {
        const numColumns = rowData.length + 1;
        const report = archived[rowMeta.dataIndex];
        return <HistoryDetailsView reportId={report?.id} numColumns={numColumns} />;
      }
    }),
    [expandedRow, archived]
  );

  return (
    <Paper sx={{ overflow: 'visible' }}>
      <Table
        customStyle={{}}
        title="Archived Reports"
        mode={mode}
        data={archived}
        columns={columns}
        options={options}
        app={APP_NAME}
        loading={archivedQuery.isFetching}
      />
    </Paper>
  );
}

export default ArchivedTab;
